/**
 * Inline operational status control for instance catalog rows (active / inactive / archived).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { updateInstance } from '@/services/aiStudioAgentService';
import { STUDIO_BINDING_STATUSES } from '@/types/aiStudioAgent';
import type { StudioBindingStatus } from '@/types/aiStudioAgent';

const SELECT_CLASS =
  'max-w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1 text-sm text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60';

export interface InstanceStatusSelectProps {
  instanceId: string;
  status: StudioBindingStatus;
  instanceName?: string;
  onUpdated?: () => void;
  onError?: () => void;
}

export const InstanceStatusSelect: React.FC<InstanceStatusSelectProps> = ({
  instanceId,
  status,
  instanceName,
  onUpdated,
  onError,
}) => {
  const { t } = useLanguage();
  const [value, setValue] = useState(status);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(status);
  }, [status]);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.stopPropagation();
      const next = event.target.value as StudioBindingStatus;
      if (next === value) return;

      if (next === 'archived') {
        const confirmed = window.confirm(
          t('aiStudio.instances.archiveConfirm', { name: instanceName ?? instanceId })
        );
        if (!confirmed) {
          event.target.value = value;
          return;
        }
      }

      setSaving(true);
      try {
        await updateInstance(instanceId, { status: next });
        setValue(next);
        onUpdated?.();
      } catch {
        setValue(status);
        event.target.value = status;
        onError?.();
      } finally {
        setSaving(false);
      }
    },
    [instanceId, instanceName, onError, onUpdated, status, t, value]
  );

  return (
    <select
      className={SELECT_CLASS}
      value={value}
      disabled={saving}
      aria-label={t('aiStudio.instances.fieldStatus')}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onChange={(event) => void handleChange(event)}
    >
      {STUDIO_BINDING_STATUSES.map((option) => (
        <option key={option} value={option}>
          {t(`aiStudio.bindingStatus.${option}`)}
        </option>
      ))}
    </select>
  );
};
